/**
 * EntityPeek.jsx — the "Open" pop-up for a thread or a milestone (Flight Plan, Tower, Triage).
 *
 * History: this used to load the WHOLE APP in an iframe (buildSplitUrl + split=1) so the pop-up
 * showed the real outline, not a rebuilt summary (Matt, 2026-09). Measured 2026-09-23: that second
 * copy of Cortex — its outline engine, gates, bell and timers — kept Matt's tab busy about 6 s of
 * every 10 (hovering a button lagged), froze the page while it booted, and re-fetched the project
 * from Airtable on every open, competing with the whole firm for the ~5 req/s base limit.
 *
 * Now a THREAD renders the canonical ThreadNode directly — the exact component the outline uses,
 * so a change to how a thread is shown lands here too with no second copy to maintain (Matt's
 * condition for the switch). A MILESTONE still uses the iframe until the milestone view is made
 * project-safe (it currently loads concepts / BIM / sheet disciplines for the project ON SCREEN,
 * not the milestone's own; see docs/FLIGHT_PLAN.md "Peek without an iframe").
 *
 * Layering: the backdrop sits at 550 — above workspaces (400) and the bell (500), below every
 * regular modal (600) so a thread's Log Activity / Edit / attachment windows open ABOVE it; the
 * Slack panel was raised to 560 for the same reason; action menus (99999) and dialogs (1000100)
 * were already above. The Triage Gate hides its own scrim while a peek is open instead of pushing
 * the peek above everything.
 */

import { useEffect, useReducer, useState } from 'react'
import { createPortal } from 'react-dom'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'
import { getThreadById } from '../services/threads.js'
import { useScrollLock } from '../hooks/useScrollLock.js'
import { buildSplitUrl } from './splitView.js'
import ThreadNode from './ThreadNode.jsx'
import { issueWorkPass } from '../outline/lockoutPass.js'

export function EntityPeekModal({ target, onClose, zIndex }) {
  useScrollLock(!!target)
  // Opened from a lockout card: the tool windows opened from here get the work pass.
  useEffect(() => { if (target) issueWorkPass() }, [target])
  useEffect(() => {
    if (!target) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [target, onClose])

  if (!target) return null
  return createPortal(
    <div style={{ ...S.backdrop, ...(zIndex ? { zIndex } : null) }} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={S.head}>
          {/* Project name in the title — Matt: "so we know at a glance what it refers to." */}
          <span style={S.title}>
            {(target.type === 'thread' ? 'Thread' : target.type === 'schedule' ? 'Schedule' : target.type === 'meeting' ? 'Meeting' : target.type === 'permits' ? 'Permits' : 'Milestone') + (target.projectName ? ' · ' + target.projectName : '')}
          </span>
          <button style={S.close} onClick={onClose} title="Close">×</button>
        </div>
        {target.type === 'thread'
          ? <ThreadPeekBody key={target.entityId} target={target} onClose={onClose} />
          : <MilestoneFrame target={target} />}
      </div>
    </div>,
    document.body
  )
}

/* The canonical thread row, expanded, fed its own record. Everything it lazy-loads (activity log,
   subtasks, working notes, attachments, a linked RFI/submittal) is cached by THREAD id, so it is
   safe for a thread from any project. The one project-wide cache it can touch — the construction
   contract list a linked RFI/submittal row reads — is set aside for the peek and restored after,
   so the project on screen never shows another project's contracts. */
function ThreadPeekBody({ target, onClose }) {
  const [, force] = useReducer(x => x + 1, 0)
  const [thread, setThread] = useState(undefined)   // undefined loading · null missing · record

  // Re-render with the outline: ThreadNode's sections signal their loads through `outline-render`.
  useEffect(() => {
    const on = () => force()
    window.addEventListener('outline-render', on)
    return () => window.removeEventListener('outline-render', on)
  }, [])

  useEffect(() => {
    // Open expanded, like the thread's own zoom showed it: the row and its Activity Log, Working
    // Notes and Attachments. The outline's own open/closed state for these is put back on close.
    const id = target.entityId
    const keys = ['thread:' + id, 'thread-acts:' + id, 'worknotes:thread:' + id, 'thread-atts:' + id]
    const prevCollapsed = keys.map(k => wf.collapsed[k])
    keys.forEach(k => { wf.collapsed[k] = false })
    const onScreenProject = ctx.recordId || wf.zoomProjectId || null
    const otherProject = !!target.projectId && target.projectId !== onScreenProject
    const savedContracts = ctx.contractCache
    if (otherProject) ctx.contractCache = undefined    // let the peek load this thread's project's
    let cancelled = false
    getThreadById(target.entityId).then(t => { if (!cancelled) setThread(t || null) }).catch(() => { if (!cancelled) setThread(null) })
    return () => {
      cancelled = true
      keys.forEach((k, i) => { if (prevCollapsed[i] === undefined) delete wf.collapsed[k]; else wf.collapsed[k] = prevCollapsed[i] })
      if (otherProject) ctx.contractCache = savedContracts
    }
  }, [target.entityId, target.projectId])

  if (thread === undefined) return <div style={S.note}>Loading…</div>
  if (thread === null) return <div style={S.note}>This thread could not be loaded. It may have been deleted.</div>
  return (
    <div style={S.body}>
      <div className="wf-list">
        <ThreadNode
          item={thread}
          projectId={target.projectId}
          projectName={target.projectName}
          // The bullet normally zooms the outline in place; from a pop-up that may be showing
          // another project, go to the thread properly instead.
          onFocus={() => { onClose(); ctx.zoomToEntity && ctx.zoomToEntity('thread', thread.id, target.projectId) }}
        />
      </div>
    </div>
  )
}

/* Milestones: still the real app in an iframe for now (see the header). key=src forces a fresh
   iframe per target rather than reusing one mid-navigation. 'schedule' is the project's whole
   Schedule with `expand` rows opened on arrival (On Deck's Open — work the whole schedule, Gantt
   included, without leaving the lockout; Matt, 2026-09-24). */
function MilestoneFrame({ target }) {
  // A meeting (a minutes revision request in triage) opens on its own minutes zoom.
  const obj = target.type === 'schedule'
    ? { p: target.projectId, r: 'schedule', x: target.expand || [] }
    : target.type === 'meeting'
    ? { p: target.projectId, r: 'meetings', sec: 'meeting-detail:' + target.entityId }
    : target.type === 'permits'
    ? { p: target.projectId, r: 'permits', ...(target.entityId ? { sec: 'review-detail:' + target.entityId } : {}) }
    : { p: target.projectId, r: 'schedule', ms: target.entityId }
  const src = buildSplitUrl(window.location.origin, obj)
  return <iframe key={src} src={src} title="Outline" style={S.frame} />
}

const S = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(7,39,45,0.4)', zIndex: 550, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { width: 'min(1100px, 94vw)', height: 'min(820px, 92vh)', display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 10, boxShadow: '0 8px 40px rgba(0,0,0,0.28)', overflow: 'hidden' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-surface)' },
  title: { fontFamily: '"Montserrat", sans-serif', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--aj-text-mid)' },
  close: { fontSize: 20, lineHeight: 1, color: 'var(--aj-text-mid)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' },
  frame: { flex: '1 1 auto', width: '100%', border: 'none', background: 'var(--aj-bg-page)' },
  body: { flex: '1 1 auto', minHeight: 0, overflowY: 'auto', padding: '14px 18px', background: 'var(--aj-bg-page)' },
  note: { padding: 28, textAlign: 'center', color: 'var(--aj-text-mid)', fontSize: 13 },
}
